'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { animate, motion, useInView, useMotionValue } from 'framer-motion'
import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'

interface FactItem {
  number: string
  title: string
  subtitle?: string
}

type SectionSettings = {
  anchorId?: string
  backgroundShade?: 'white' | 'neutral-50' | 'neutral-100'
  isDarkMode?: boolean
  padding?: 'md' | 'lg'
}

type HeaderGroup = {
  infoTagText?: string
  title: string
  subtitle?: string
}

type FactsBlockProps = {
  header: HeaderGroup
  facts: FactItem[]
  settings?: SectionSettings
}

function splitNumericPrefix(value: string): {
  numeric: number | null
  suffix: string
} {
  const match = value.match(/^(\d+)(.*)$/)
  if (!match) return { numeric: null, suffix: '' }
  return { numeric: Number(match[1]), suffix: match[2] }
}

function FactCard({ number, title, subtitle, delay = 0 }: FactItem & { delay?: number }) {
  const { numeric, suffix } = useMemo(() => splitNumericPrefix(number), [number])
  const ref = useRef<HTMLDivElement | null>(null)
  const inView = useInView(ref, { once: true, amount: 0.5 })
  const motionValue = useMotionValue(numeric ?? 0)
  const [display, setDisplay] = useState<number>(numeric ?? 0)

  useEffect(() => {
    const unsubscribe = motionValue.on('change', (v) => setDisplay(Math.round(v)))
    return () => unsubscribe()
  }, [motionValue])

  useEffect(() => {
    if (inView && typeof numeric === 'number') {
      motionValue.set(0)
      const controls = animate(motionValue, numeric, {
        duration: 1.6,
        ease: [0.16, 1, 0.3, 1],
      })
      return () => controls.stop()
    }
  }, [inView, numeric, motionValue])

  return (
    <motion.div
      ref={ref}
      className="bg-[var(--card)] px-6 md:px-8 py-5 md:py-6 h-full"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay }}
    >
      <div className="display-md text-brand">
        {typeof numeric === 'number' ? `${display}${suffix}` : number}
      </div>
      <div className="mt-6 md:mt-8 flex flex-col">
        <div className="title-sm text-primary uppercase">{title}</div>
        {subtitle && <div className="body-base text-text-secondary">{subtitle}</div>}
      </div>
    </motion.div>
  )
}

export default function FactsBlock({ header, facts, settings }: FactsBlockProps) {
  const normalizedSettings = normalizeSectionSettings(settings)

  return (
    <SectionWrapper settings={normalizedSettings}>
      {/* Header */}
      <SectionHeader
        infoTagText={header?.infoTagText ?? ''}
        title={header?.title ?? ''}
        subtitle={header?.subtitle}
        align="left"
        size="md"
        className="mb-8"
        subtitleClassName="subtitle-lg"
      />

      {/* Cards */}
      {facts && facts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {facts.map((fact, index) => (
            <FactCard
              key={fact.number || index}
              number={fact.number}
              title={fact.title}
              subtitle={fact.subtitle}
              delay={index * 0.12}
            />
          ))}
        </div>
      )}
    </SectionWrapper>
  )
}
